/*
PURPOSE: Artist-facing verification and payout collection page (/a/:artistUrl/collect).
Editorial restyle — U5 of the PublicArtist editorial migration.

USE CASES:
- Artists verifying their identity to collect fan contributions via a secure link.
- Artists viewing ecosystem vitals and estimated payout via ArtistPayoutChecker.
- Artists reviewing "How It Works" bullets and platform-comparison tooltip.

DOMAIN BOUNDARIES:
- Handles the full verification flow: fetch artist, render form, submit via verifyArtist().
- Does NOT handle payment processing itself.
- Uses PublicPageErrorBoundary so any render fault degrades gracefully.
- Copy consumed entirely via useCopy().publicPages.payoutCollection.
- faqForArtists (artist-targeted FAQ) is correct for this audience.

LIFECYCLE:
- Artist fetch: cancelled-boolean pattern (getArtistDetailsPublic does not accept
  AbortSignal). All setState calls are guarded by `if (cancelled) return`.
- handleSubmit: single-shot, no cleanup needed. isSubmitting flag prevents double-submit.
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { verifyArtist, getArtistDetailsPublic } from '../../../api/api';
import { useCopy } from '../../../hooks/useCopy';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import { BlendedArtistListItem } from 'shared/types/platformTypes';
import ArtistPayoutChecker from '../../../components/ArtistPayoutChecker/ArtistPayoutChecker';
import Spinner from '../../../components/Spinner/Spinner';
import { faqData } from '../ForArtists/faqForArtists';
import FAQItem from '../../../components/FAQItem/faqItem';
import EcosystemBloom from '../../../components/EcosystemBloom/EcosystemBloom';
import EcosystemVitals from '../../../components/EcosystemVitals/EcosystemVitals';
import { Tooltip } from 'react-tooltip';
import PublicPageErrorBoundary from '../../../components/PublicPageErrorBoundary/PublicPageErrorBoundary';
import {
  EditorialSection,
  Eyebrow,
  DisplayHeading,
  BodyProse,
  CtaPill,
  WhyCoralSection,
} from '../../../components/editorial';

interface VerificationFormData {
  verificationCode: string;
  email: string;
  paypalEmail: string;
}

// Extended artist interface with ecosystem data
interface ArtistEcosystemData extends BlendedArtistListItem {
  ecosystemSize?: number;
  totalSupporters?: number;
  monthlySupport?: number;
  retentionRate?: number;
}

const ArtistPayoutCollection: React.FC = () => {
  const { artistUrl } = useParams() as { artistUrl: string };
  const location = useLocation();
  const copy = useCopy().publicPages.payoutCollection;
  // Vision is single-sourced from publicArtist.vision so the PublicArtist-subtree
  // pages cannot drift on the vision statement.
  const visionCopy = useCopy().publicPages.publicArtist.vision;
  const [formData, setFormData] = useState<VerificationFormData>({
    verificationCode: '',
    email: '',
    paypalEmail: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [artist, setArtist] = useState<ArtistEcosystemData | null>(null);
  const [loading, setLoading] = useState(true);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Mock ecosystem data for display purposes (pre-existing defect — flagged in plan Risks, not fixed here)
  const mockEcosystemData: Partial<ArtistEcosystemData> = useMemo(() => ({
    ecosystemSize: 342,
    totalSupporters: 342,
    monthlySupport: 2890,
    retentionRate: 94,
  }), []);

  useEffect(() => {
    // cancelled-boolean pattern: getArtistDetailsPublic does not accept AbortSignal.
    // All setState calls below are guarded by `if (cancelled) return`.
    let cancelled = false;

    const fetchArtistData = async () => {
      try {
        const data = await getArtistDetailsPublic(artistUrl);
        if (cancelled) return;
        if (data && data.length > 0) {
          setArtist({
            ...data[0],
            ...mockEcosystemData
          });
          // Track view after successful artist fetch
          trackEvent(EventName.ARTIST_VERIFICATION_VIEW, {
            artistUrl
          });
        } else {
          console.warn('No artist data returned');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching artist:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (artistUrl) {
      setLoading(true);
      fetchArtistData();
    }

    return () => {
      cancelled = true;
    };
  }, [artistUrl, mockEcosystemData]);

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAgreed(e.target.checked);
    if (e.target.checked) {
      setValidationError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!termsAgreed) {
      setValidationError(copy.form.validationError);
      return;
    }

    setIsSubmitting(true);

    const params = new URLSearchParams(location.search);
    const verificationUrl = params.get('verifyUrl');

    if (!verificationUrl) {
      setResult({
        success: false,
        message: copy.form.missingUrlError
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await verifyArtist({
        verificationUrl,
        ...formData
      });

      setResult(response);
      trackEvent(EventName.ARTIST_VERIFICATION_COMPLETE, {
        artistUrl,
        success: response.success
      });
    } catch (error) {
      const errorResult = {
        success: false,
        message: copy.form.genericError
      };
      setResult(errorResult);
      trackEvent(EventName.ARTIST_VERIFICATION_COMPLETE, {
        artistUrl,
        success: false
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state: editorial ivory background so the branded surface shows during load
  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-ivory">
        <EditorialSection background="ivory" topRule={false}>
          <div className="flex items-center justify-center py-24">
            <Spinner />
          </div>
        </EditorialSection>
      </div>
    );
  }

  // Not-found state: editorial treatment, not legacy navy
  if (!artist) {
    return (
      <div className="flex flex-col min-h-screen bg-ivory">
        <PublicPageErrorBoundary>
          <EditorialSection background="ivory" topRule={false}>
            <DisplayHeading as="h1">{copy.notFound.title}</DisplayHeading>
            <BodyProse className="mt-6">
              <p>{copy.notFound.message}</p>
            </BodyProse>
            <div className="mt-10">
              <CtaPill href="/">{copy.notFound.cta}</CtaPill>
            </div>
          </EditorialSection>
        </PublicPageErrorBoundary>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-ivory">
      <PublicPageErrorBoundary>

        {/* Hero section — two-column: left = copy + how it works, right = vitals + checker + form */}
        <EditorialSection background="ivory" topRule={false} id="payout-hero">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">

            {/* Left column */}
            <div>
              <DisplayHeading as="h1" scale="display">
                {artist?.artistName}
              </DisplayHeading>
              <BodyProse className="mt-6">
                <p>
                  {copy.hero.subtitleLine1}<br />{copy.hero.subtitleLine2}
                </p>
                <p className="mt-4">{copy.hero.collectIntro}</p>
              </BodyProse>

              {/* How It Works */}
              <div className="mt-10">
                <Eyebrow>{copy.howItWorks.header}</Eyebrow>
                <ul className="mt-4 space-y-3">
                  <li className="flex items-start gap-3 text-editorial-body text-editorial-muted/80">
                    <span className="text-coral-orange mt-1 flex-shrink-0" aria-hidden="true">•</span>
                    <span>{copy.howItWorks.bullets.first}</span>
                  </li>
                  <li className="flex items-start gap-3 text-editorial-body text-editorial-muted/80">
                    <span className="text-coral-orange mt-1 flex-shrink-0" aria-hidden="true">•</span>
                    <span>{copy.howItWorks.bullets.second}</span>
                  </li>
                  <li className="flex items-start gap-3 text-editorial-body text-editorial-muted/80">
                    <span className="text-coral-orange mt-1 flex-shrink-0" aria-hidden="true">•</span>
                    <span>{copy.howItWorks.bullets.third}</span>
                  </li>
                </ul>

                {/* Platform comparison tooltip — button for keyboard accessibility (T4) */}
                <div className="mt-6 text-editorial-body text-editorial-muted/80">
                  {copy.platformComparison.prefix}
                  <button
                    type="button"
                    data-tooltip-id="compareTooltip"
                    data-tooltip-html={copy.platformComparison.tooltipHtml}
                    className="underline text-editorial-accent hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-coral-orange focus:ring-offset-1 rounded-sm"
                    aria-describedby="compareTooltip"
                  >
                    {copy.platformComparison.linkText}
                  </button>
                  <Tooltip id="compareTooltip" className="z-50" />
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="flex flex-col gap-8">
              <EcosystemVitals artistName={artist?.artistName || ''} />
              <ArtistPayoutChecker presetArtist={artist?.artistName} />

              {/* Form card or result panel */}
              {!result ? (
                <form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-3xl border border-editorial-muted/10 shadow-[0_24px_48px_-24px_rgba(12,19,45,0.12)] p-7 md:p-9 w-full"
                >
                  <h2 className="font-cormorant font-normal text-editorial-muted text-editorial-heading mb-8">
                    {copy.form.title}
                  </h2>

                  <div className="space-y-6">
                    {/* Verification Code */}
                    <div>
                      <label
                        htmlFor="verificationCode"
                        className="block text-[11px] tracking-[0.18em] uppercase text-editorial-muted/60 mb-2"
                      >
                        {copy.form.codeLabel}
                      </label>
                      <input
                        id="verificationCode"
                        type="text"
                        value={formData.verificationCode}
                        onChange={(e) => setFormData({ ...formData, verificationCode: e.target.value })}
                        className="w-full border-b border-editorial-muted/20 bg-transparent py-3 font-cormorant text-editorial-display text-2xl focus:outline-none focus:border-editorial-accent transition-colors"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-[11px] tracking-[0.18em] uppercase text-editorial-muted/60 mb-2"
                      >
                        {copy.form.emailLabel}
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full border-b border-editorial-muted/20 bg-transparent py-3 font-cormorant text-editorial-display text-2xl focus:outline-none focus:border-editorial-accent transition-colors"
                        required
                      />
                    </div>

                    {/* PayPal Email */}
                    <div>
                      <label
                        htmlFor="paypalEmail"
                        className="block text-[11px] tracking-[0.18em] uppercase text-editorial-muted/60 mb-2"
                      >
                        {copy.form.paypalLabel}
                      </label>
                      <input
                        id="paypalEmail"
                        type="email"
                        value={formData.paypalEmail}
                        onChange={(e) => setFormData({ ...formData, paypalEmail: e.target.value })}
                        className="w-full border-b border-editorial-muted/20 bg-transparent py-3 font-cormorant text-editorial-display text-2xl focus:outline-none focus:border-editorial-accent transition-colors"
                        required
                      />
                      <p className="mt-2 text-[11px] tracking-[0.12em] text-editorial-muted/60">
                        {copy.form.paypalHelp}
                      </p>
                    </div>
                  </div>

                  {/* Terms and conditions */}
                  <div className="mt-8">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="termsAgreed"
                        checked={termsAgreed}
                        onChange={handleTermsChange}
                        className="mt-1 accent-coral-orange flex-shrink-0"
                      />
                      <label htmlFor="termsAgreed" className="text-editorial-body text-editorial-muted/80 leading-relaxed">
                        {copy.form.termsPrefix}
                        <a
                          href="/artist-terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-editorial-accent hover:opacity-80"
                        >
                          {copy.form.termsLinkText}
                        </a>
                      </label>
                    </div>
                    {validationError && (
                      <p className="text-red-600 text-sm mt-2">{validationError}</p>
                    )}
                  </div>

                  {/* Submit button — real form submit semantics, editorial pill style */}
                  <button
                    type="submit"
                    disabled={isSubmitting || !termsAgreed}
                    className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-coral-orange hover:bg-coral-orange/90 text-editorial-muted rounded-full px-6 py-3 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? copy.form.submittingLabel : copy.form.submitLabel}
                  </button>
                </form>
              ) : (
                // Result panel — role="status" for a11y (T4)
                <div role="status">
                  {result.success ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
                      <p className="text-editorial-body text-editorial-muted/80">{result.message}</p>
                      <p className="mt-3 text-editorial-body text-editorial-muted/80">{copy.result.successFollowUp}</p>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                      <p className="text-editorial-body text-editorial-muted/80">{result.message}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </EditorialSection>

        {/* Why Coral Exists — shared editorial section, mirrors Home + PublicArtist */}
        <WhyCoralSection />

        {/* Vision section — single-sourced from publicArtist.vision */}
        <EditorialSection background="ivory" id="payout-vision">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>{visionCopy.sectionTitle}</Eyebrow>
              <DisplayHeading as="h2" scale="heading" className="mt-8" measure={false}>
                {visionCopy.description}
              </DisplayHeading>
            </div>
            <div>
              <EcosystemBloom />
            </div>
          </div>
        </EditorialSection>

        {/* FAQ section — artist-targeted FAQ (faqForArtists) correct for this audience */}
        <EditorialSection background="cream" id="payout-faq">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            <div>
              <Eyebrow>{copy.support.sectionTitle}</Eyebrow>
              <DisplayHeading as="h2" scale="heading" className="mt-8">
                {copy.support.faqTitle}
              </DisplayHeading>
              <BodyProse className="mt-6">
                <p>{copy.support.faqDescription}</p>
              </BodyProse>
            </div>
            <div className="space-y-2 [&_button_span]:!text-editorial-ink [&_button+div]:!text-editorial-muted/80">
              {faqData.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </EditorialSection>

      </PublicPageErrorBoundary>
    </div>
  );
};

export default ArtistPayoutCollection;
